/**
 * KRCommentSection Component
 * Inline expandable comment section for Key Results
 */

import React, { useState, useCallback } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { ChevronDown, ChevronUp, MessageSquare, Reply, Send } from 'lucide-react';
import { Avatar, Button, LoadingSpinner, MentionTextarea } from '../common';
import { useKeyResultComments, useCreateComment } from '../../hooks';
import { useAuth } from '../../context/AuthContext';
import type { Comment, MentionUser } from '../../types/comment';

export interface KRCommentSectionProps {
  keyResultId: string;
}

export const KRCommentSection: React.FC<KRCommentSectionProps> = ({
  keyResultId
}) => {
  const { user } = useAuth();
  const { data: comments, isLoading, error } = useKeyResultComments(keyResultId);
  const createCommentMutation = useCreateComment();

  const [isExpanded, setIsExpanded] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [mentionedUsers, setMentionedUsers] = useState<MentionUser[]>([]);
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [replyContent, setReplyContent] = useState('');
  const [replyMentionedUsers, setReplyMentionedUsers] = useState<MentionUser[]>([]);

  const totalComments = comments?.reduce((acc, comment) => {
    return acc + 1 + (comment.replies?.length || 0);
  }, 0) || 0;

  const handleSubmitComment = async () => {
    if (!newComment.trim()) return;

    await createCommentMutation.mutateAsync({
      keyResultId,
      content: newComment.trim(),
      mentionedUserIds: mentionedUsers.map(u => u.id)
    });

    setNewComment('');
    setMentionedUsers([]);
  };

  const handleMentionsChange = useCallback((users: MentionUser[]) => {
    setMentionedUsers(users);
  }, []);

  const handleSubmitReply = async (parentCommentId: string) => {
    if (!replyContent.trim()) return;

    await createCommentMutation.mutateAsync({
      keyResultId,
      content: replyContent.trim(),
      parentCommentId,
      mentionedUserIds: replyMentionedUsers.map(u => u.id)
    });

    setReplyContent('');
    setReplyingTo(null);
    setReplyMentionedUsers([]);
  };

  const handleReplyMentionsChange = useCallback((users: MentionUser[]) => {
    setReplyMentionedUsers(users);
  }, []);

  const handleKeyDown = (
    e: React.KeyboardEvent,
    submitFn: () => void
  ) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      submitFn();
    }
  };

  const toggleExpand = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering parent click handlers (like KR card edit mode)
    setIsExpanded(!isExpanded);
    // Reset form state when collapsing
    if (isExpanded) {
      setNewComment('');
      setMentionedUsers([]);
      setReplyingTo(null);
      setReplyContent('');
      setReplyMentionedUsers([]);
    }
  };

  // Prevent clicks within the comment section from bubbling to parent
  const handleSectionClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  return (
    <div className="mt-3 border-t border-stone-100 pt-3" onClick={handleSectionClick}>
      {/* Toggle Button */}
      <button
        type="button"
        onClick={toggleExpand}
        className="flex items-center gap-2 text-sm text-stone-600 hover:text-primary-600 transition-colors"
      >
        <MessageSquare className="h-4 w-4" />
        <span className="font-medium">
          Comments {totalComments > 0 && `(${totalComments})`}
        </span>
        {isExpanded ? (
          <ChevronUp className="h-4 w-4" />
        ) : (
          <ChevronDown className="h-4 w-4" />
        )}
      </button>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="mt-3 space-y-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-4">
              <LoadingSpinner size="sm" />
            </div>
          ) : error ? (
            <p className="text-sm text-red-600 py-2">Failed to load comments</p>
          ) : (
            <>
              {/* Add Comment Form */}
              <div className="flex gap-2">
                <Avatar
                  fallbackText={`${user?.firstName} ${user?.lastName}`}
                  size="sm"
                />
                <div className="flex-1">
                  <MentionTextarea
                    value={newComment}
                    onChange={setNewComment}
                    onMentionsChange={handleMentionsChange}
                    placeholder="Add a comment..."
                    rows={2}
                    onKeyDown={(e) => handleKeyDown(e, handleSubmitComment)}
                  />
                  <div className="mt-2 flex justify-end">
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={handleSubmitComment}
                      disabled={!newComment.trim() || createCommentMutation.isPending}
                    >
                      {createCommentMutation.isPending ? (
                        <LoadingSpinner size="sm" />
                      ) : (
                        <>
                          <Send className="h-3 w-3 mr-1" />
                          Comment
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </div>

              {/* Comments List */}
              {comments && comments.length > 0 ? (
                <div className="space-y-4 max-h-64 overflow-y-auto">
                  {comments.map((comment) => (
                    <KRCommentItem
                      key={comment.id}
                      comment={comment}
                      replyingTo={replyingTo}
                      replyContent={replyContent}
                      onReplyClick={(id) => {
                        setReplyingTo(replyingTo === id ? null : id);
                        setReplyContent('');
                        setReplyMentionedUsers([]);
                      }}
                      onReplyContentChange={setReplyContent}
                      onReplyMentionsChange={handleReplyMentionsChange}
                      onSubmitReply={handleSubmitReply}
                      isSubmitting={createCommentMutation.isPending}
                    />
                  ))}
                </div>
              ) : (
                <p className="text-sm text-stone-500 py-2 text-center">
                  No comments yet. Be the first to comment!
                </p>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

// Sub-component for individual comment
interface KRCommentItemProps {
  comment: Comment;
  replyingTo: string | null;
  replyContent: string;
  onReplyClick: (id: string) => void;
  onReplyContentChange: (content: string) => void;
  onReplyMentionsChange: (users: MentionUser[]) => void;
  onSubmitReply: (parentId: string) => void;
  isSubmitting: boolean;
}

const KRCommentItem: React.FC<KRCommentItemProps> = ({
  comment,
  replyingTo,
  replyContent,
  onReplyClick,
  onReplyContentChange,
  onReplyMentionsChange,
  onSubmitReply,
  isSubmitting
}) => {
  const isReplying = replyingTo === comment.id;

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      onSubmitReply(comment.id);
    }
  };

  return (
    <div className="relative">
      {/* Main Comment */}
      <div className="flex gap-2">
        <Avatar
          fallbackText={`${comment.user.firstName} ${comment.user.lastName}`}
          size="sm"
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <span className="font-medium text-stone-900 text-sm truncate">
              {comment.user.firstName} {comment.user.lastName}
            </span>
            <time className="text-xs text-stone-400 flex-shrink-0">
              {formatDistanceToNow(new Date(comment.createdAt), { addSuffix: true })}
            </time>
          </div>
          <p className="text-sm text-stone-700 whitespace-pre-wrap break-words">
            {comment.content}
          </p>
          <button
            type="button"
            onClick={() => onReplyClick(comment.id)}
            className="mt-1 inline-flex items-center text-xs text-stone-400 hover:text-primary-600"
          >
            <Reply className="h-3 w-3 mr-1" />
            Reply
          </button>
        </div>
      </div>

      {/* Reply Form */}
      {isReplying && (
        <div className="mt-2 ml-8 flex gap-2">
          <div className="flex-1">
            <MentionTextarea
              value={replyContent}
              onChange={onReplyContentChange}
              onMentionsChange={onReplyMentionsChange}
              placeholder="Write a reply..."
              rows={2}
              autoFocus
              onKeyDown={handleKeyDown}
            />
            <div className="mt-1 flex justify-end gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => onReplyClick(comment.id)}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={() => onSubmitReply(comment.id)}
                disabled={!replyContent.trim() || isSubmitting}
              >
                {isSubmitting ? <LoadingSpinner size="sm" /> : 'Reply'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Replies */}
      {comment.replies && comment.replies.length > 0 && (
        <div className="mt-2 ml-8 space-y-2 border-l-2 border-stone-100 pl-3">
          {comment.replies.map((reply) => (
            <div key={reply.id} className="flex gap-2">
              <Avatar
                fallbackText={`${reply.user.firstName} ${reply.user.lastName}`}
                size="xs"
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="font-medium text-stone-900 text-xs truncate">
                    {reply.user.firstName} {reply.user.lastName}
                  </span>
                  <time className="text-xs text-stone-400 flex-shrink-0">
                    {formatDistanceToNow(new Date(reply.createdAt), { addSuffix: true })}
                  </time>
                </div>
                <p className="text-xs text-stone-700 whitespace-pre-wrap break-words">
                  {reply.content}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default KRCommentSection;
